import React, { useState, useEffect, useContext, createContext } from 'react';

// Updated theme data with more warm orange/peach tones to match the reference image
const themes = [
  {
    name: "Peach",
    colors: [
      "rgba(255, 179, 138, 0.5)",   // Light peach
      "rgba(255, 153, 102, 0.4)",   // Medium peach
      "rgba(255, 128, 80, 0.3)",    // Darker peach
      "rgba(255, 204, 153, 0.4)",   // Very light peach
      "rgba(255, 102, 51, 0.3)",    // Orange
      "rgba(255, 230, 204, 0.6)"    // Cream
    ]
  },
  {
    name: "Orange",
    colors: [
      "rgba(255, 90, 0, 0.3)",
      "rgba(255, 90, 0, 0.4)",
      "rgba(255, 90, 0, 0.5)",
      "rgba(255, 90, 0, 0.6)",
      "rgba(255, 90, 0, 0.7)",
      "rgba(255, 90, 0, 0.8)"
    ]
  },
  {
    name: "EosYes",
    colors: [
      "rgba(255, 90, 0, 0.7)",      // Bright orange
      "rgba(220, 20, 60, 0.5)",     // Scarlet/crimson 
      "rgba(0, 32, 91, 0.6)",       // Navy blue
      "rgba(255, 140, 0, 0.4)",     // Dark orange
      "rgba(128, 0, 32, 0.5)",      // Burgundy (between scarlet and navy)
      "rgba(70, 130, 180, 0.4)"     // Steel blue (lighter navy)
    ]
  },
  {
    name: "Scarlet",
    colors: [
      "rgba(220, 20, 60, 0.3)",
      "rgba(220, 20, 60, 0.4)",
      "rgba(220, 20, 60, 0.5)",
      "rgba(220, 20, 60, 0.6)",
      "rgba(220, 20, 60, 0.7)",
      "rgba(220, 20, 60, 0.8)"
    ]
  },
  {
    name: "Navy",
    colors: [
      "rgba(0, 32, 91, 0.3)",
      "rgba(0, 32, 91, 0.4)",
      "rgba(0, 32, 91, 0.5)",
      "rgba(0, 32, 91, 0.6)",
      "rgba(0, 32, 91, 0.7)",
      "rgba(0, 32, 91, 0.8)"
    ]
  }
];

// Create a context for the theme
export const ThemeContext = createContext();

// Theme provider
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(themes[0]);

  useEffect(() => {
    // Load saved theme
    const savedThemeName = localStorage.getItem('selectedThemeName');
    if (savedThemeName) {
      const savedTheme = themes.find(theme => theme.name === savedThemeName);
      if (savedTheme) {
        setCurrentTheme(savedTheme);
      }
    }
  }, []);

  const applyTheme = (theme) => {
    setCurrentTheme(theme);
    localStorage.setItem('selectedThemeName', theme.name);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, applyTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper function to generate random position
const getRandomPosition = (width, height) => {
  return {
    x: Math.random() * width,
    y: Math.random() * height
  };
};

const AnimatedBackground = () => {
  const { currentTheme } = useContext(ThemeContext) || { currentTheme: themes[0] };
  const [dimensions, setDimensions] = useState({ 
    width: typeof window !== 'undefined' ? window.innerWidth : 1000, 
    height: typeof window !== 'undefined' ? window.innerHeight : 800 
  });
  const [positions, setPositions] = useState([]);

  // Initialize random positions on mount
  useEffect(() => {
    // Generate random initial positions
    const initialPositions = Array(6).fill(0).map(() => 
      getRandomPosition(dimensions.width, dimensions.height)
    );
    setPositions(initialPositions);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animate positions - very slow movement for subtle effect
  useEffect(() => {
    // Skip if positions haven't been initialized yet
    if (positions.length === 0) return;

    const timer = setInterval(() => {
      setPositions(prevPositions => prevPositions.map((pos, index) => {
        if (index === 3) {
          return { x: Math.random() * dimensions.width, y: dimensions.height };
        } else if (index === 4) {
          return { x: Math.random() * dimensions.width, y: 0 };
        } else {
          return getRandomPosition(dimensions.width, dimensions.height);
        }
      }));
    }, 8000); // Even slower movement for subtlety

    return () => clearInterval(timer);
  }, [dimensions, positions]);

  const colors = currentTheme.colors;

  // Don't render until positions are initialized
  if (positions.length === 0) return null;

  return (
    <div className="fixed inset-0 overflow-hidden z-0 bg-orange-50">
      <div className="absolute inset-0">
        {positions.map((position, index) => (
          <div 
            key={index}
            className="absolute rounded-full transition-all ease-in-out" 
            style={{
              backgroundColor: colors[index] || 'transparent',
              // Make circles larger for the peach theme
              width: `${dimensions.width / (index === 5 ? 3 : 2)}px`,
              height: `${dimensions.width / (index === 5 ? 3 : 2)}px`,
              left: position.x,
              top: position.y,
              // Slower transitions for very subtle movement
              transition: `left ${(40 - index * 2) || 30}s, top ${(40 - index * 2) || 30}s`,
              filter: index % 2 === 0 ? 'blur(70px)' : 'blur(50px)',
              transform: 'translate(-50%, -50%)'
            }}
          />
        ))}
      </div>
      {/* Subtle blur for softer appearance */}
      <div className="absolute inset-0" style={{ backdropFilter: 'blur(90px)' }}></div>
    </div>
  );
};

export default AnimatedBackground; 